import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminGetMaintenance, adminSaveMaintenance, fetchOrgStats, saveOrgStats } from '../../services/site';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';

const SiteSettings = () => {
  const { token } = useAuth();
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '', image_url: '' });
  const [saving, setSaving] = useState(false);

  // Statistiques de l'organisation
  const [stats, setStats] = useState({ members: '', years: '', countries: '' });
  const [savingStats, setSavingStats] = useState(false);
  const [statsMsg, setStatsMsg] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const maint = await adminGetMaintenance(token);
        setMaintenance({ enabled: !!maint.enabled, message: maint.message || '', image_url: maint.image_url || '' });
      } catch (e) { console.error('SiteSettings: échec chargement maintenance', e); }
      try {
        const s = await fetchOrgStats(token);
        setStats({
          members: s.members ?? '',
          years: s.years ?? '',
          countries: (s.countries || []).join('\n'),
        });
      } catch (e) { console.error('SiteSettings: échec chargement statistiques', e); }
    };
    load();
  }, [token]);

  const saveMaintenanceSettings = async () => {
    setSaving(true);
    try {
      await adminSaveMaintenance(maintenance, token);
      alert('Paramètres enregistrés');
    } catch (e) {
      alert('Erreur de sauvegarde');
    } finally { setSaving(false); }
  };

  const saveStatsSettings = async () => {
    setSavingStats(true); setStatsMsg('');
    try {
      const countries = stats.countries
        .split(/[\n,]/)
        .map((c) => c.trim())
        .filter(Boolean);
      const res = await saveOrgStats({
        members: stats.members === '' ? null : Number(stats.members),
        years: stats.years === '' ? null : Number(stats.years),
        countries,
      }, token);
      setStats((prev) => ({ ...prev, countries: (res.org_stats.countries || []).join('\n') }));
      setStatsMsg(`✅ Statistiques enregistrées : ${res.org_stats.members} membres, ${countries.length} pays, ${res.org_stats.years} ans.`);
    } catch (e) {
      setStatsMsg('❌ ' + (e.response?.data?.detail || e.message));
    } finally { setSavingStats(false); }
  };

  const countryCount = stats.countries.split(/[\n,]/).map((c) => c.trim()).filter(Boolean).length;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-2">Paramètres du site</h1>
        <p className="text-sm text-muted-foreground">Gestion du mode maintenance et raccourcis d'administration.</p>
      </div>

      <div className="border rounded-lg p-4 space-y-4">
        <h2 className="font-semibold">Mode Maintenance</h2>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={maintenance.enabled} onChange={e => setMaintenance({ ...maintenance, enabled: e.target.checked })} />
            Activer
          </label>
          <div>
            <div className="text-sm mb-1">Message</div>
            <textarea className="w-full rounded border bg-background px-3 py-2 h-24" value={maintenance.message} onChange={e => setMaintenance({ ...maintenance, message: e.target.value })} />
          </div>
          <div>
            <div className="text-sm mb-1">Image (URL)</div>
            <input className="w-full rounded border bg-background px-3 py-2" placeholder="https://.../maintenance.png" value={maintenance.image_url} onChange={e => setMaintenance({ ...maintenance, image_url: e.target.value })} />
          </div>
          <div className="flex justify-end">
            <Button onClick={saveMaintenanceSettings} disabled={saving}>{saving ? 'Sauvegarde…' : 'Sauvegarder'}</Button>
          </div>
        </div>
      </div>

      <div className="border rounded-lg p-4 space-y-4" data-testid="org-stats-editor">
        <h2 className="font-semibold">Statistiques de l'organisation</h2>
        <p className="text-sm text-muted-foreground">Ces chiffres s'affichent sur le site (page d'accueil). Le nombre de pays est calculé automatiquement à partir de la liste.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="text-sm mb-1">Nombre de membres opérants</div>
            <input data-testid="org-stats-members-input" type="number" min="0" className="w-full rounded border bg-background px-3 py-2" value={stats.members} onChange={e => setStats({ ...stats, members: e.target.value })} />
          </div>
          <div>
            <div className="text-sm mb-1">Années d'action</div>
            <input data-testid="org-stats-years-input" type="number" min="0" className="w-full rounded border bg-background px-3 py-2" value={stats.years} onChange={e => setStats({ ...stats, years: e.target.value })} />
          </div>
        </div>
        <div>
          <div className="text-sm mb-1">Pays d'intervention <span className="text-muted-foreground">(un par ligne — {countryCount} pays)</span></div>
          <textarea data-testid="org-stats-countries-input" className="w-full rounded border bg-background px-3 py-2 h-40" placeholder={"France\nItalie\nEspagne\n…"} value={stats.countries} onChange={e => setStats({ ...stats, countries: e.target.value })} />
        </div>
        {statsMsg && <div className="text-sm">{statsMsg}</div>}
        <div className="flex justify-end">
          <Button data-testid="save-org-stats-btn" onClick={saveStatsSettings} disabled={savingStats}>{savingStats ? 'Sauvegarde…' : 'Sauvegarder les statistiques'}</Button>
        </div>
      </div>

      <div className="border rounded-lg p-4">
        <h2 className="font-semibold mb-2">Raccourcis</h2>
        <div className="flex gap-3">
          <Button asChild variant="outline">
            <Link to="/admin/team">Gérer l'équipe</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/admin/audit/users">Journal utilisateurs</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SiteSettings;